import React, { useState, useEffect, useRef } from 'react';
import MaterialTable from 'material-table';
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import {colors} from '../components/Theme/WebTheme';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

export default function Promos() {
  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    editPromo
  } = api;
  const settings = useSelector(state => state.settingsdata.settings);

  const inputRef = useRef(null);

  const columns = [
    { title: t('promo_name'), field: 'promo_name',cellStyle:{textAlign:isRTL ==='rtl'? 'right':'left'}},
    { title: t('description'), field: 'promo_description',cellStyle:{textAlign:'center'},headerStyle:{textAlign:'center'}},
    {
      title: t('type'),
      field: 'promo_discount_type',
      lookup: { flat: t('flat'), percentage: t('percentage')},
      cellStyle:{textAlign:isRTL ==='rtl'? 'right':'left'}
    },
    { title: t('promo_discount_value'), field: 'promo_discount_value', type: 'numeric',cellStyle:{textAlign:'center'},headerStyle:{textAlign:'center'}},
    { title: t('max_limit'), field: 'max_promo_discount_value', type: 'numeric',cellStyle:{textAlign:'center'},headerStyle:{textAlign:'center'}},
    { title: t('min_limit'), field: 'min_order', type: 'numeric',cellStyle:{textAlign:'center'},headerStyle:{textAlign:'center'}},
    { title: t('end_date'), field: 'promo_validity',  
    initialEditValue: new Date().getTime(),
    editComponent: props => {
      // Convert timestamp/Date to Dayjs object, or use current date if null/undefined
      const getDayjsValue = () => {
        // Use props.value first (for new rows), then props.rowData.promo_validity (for existing rows)
        const value = props.value !== undefined ? props.value : (props.rowData?.promo_validity || new Date().getTime());
        
        if (value) {
          // If it's a timestamp (number), convert it
          if (typeof value === 'number') {
            return dayjs(value);
          }
          // If it's already a Dayjs object, return it
          if (dayjs.isDayjs(value)) {
            return value;
          }
          // If it's a Date object or string, convert it
          return dayjs(value);
        }
        // Default to current date
        return dayjs();
      };

      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label={t('end_date')}
            value={getDayjsValue()}
            onChange={(newValue) => {
              // Convert Dayjs object to timestamp (number) for storage
              if (newValue && dayjs.isDayjs(newValue)) {
                props.onChange(newValue.valueOf());
              } else {
                // Set default value if null
                props.onChange(new Date().getTime());
              }
            }}
            slotProps={{
              textField: {
                fullWidth: true,
              }
            }}
          />
        </LocalizationProvider>
      );
    },
    render: rowData => rowData.promo_validity?moment(rowData.promo_validity).format('lll'):null,cellStyle:{textAlign:'center', width: 200},headerStyle:{textAlign:'center'} },
    { title: t('promo_usage'), field: 'promo_usage_limit', type: 'numeric',cellStyle:{textAlign:'center'},headerStyle:{textAlign:'center'}},
    { title: t('promo_used_by'), field: 'user_avail', editable: 'never',cellStyle:{textAlign:'center'},headerStyle:{textAlign:'center'}}
  ];

  const [data, setData] = useState([]);
  const promodata = useSelector(state => state.promodata);
  const dispatch = useDispatch();

  useEffect(() => {
    if (promodata.promos) {
      setData(promodata.promos);
    } else {
      setData([]);
    }
  }, [promodata.promos]);

  const [selectedRow, setSelectedRow] = useState(null);

  return (
    promodata.loading ? <CircularLoading /> :
    <MaterialTable
      title={t('promo_offer')}
      columns={columns}
      style={{direction:isRTL ==='rtl'?'rtl':'ltr', borderRadius: "8px", boxShadow: "4px 4px 6px #9E9E9E"}}
      data={data}
      onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
      options={{
        rowStyle: rowData => ({
          backgroundColor: (selectedRow === rowData.tableData.id) ?  colors.THIRDCOLOR: colors.WHITE
      }),
        editable:{
          backgroundColor: colors.WHITE,
          fontSize: "0.8em",
          fontWeight: 'bold ',
          fontFamily: 'Lucida Console", "Courier New", monospace'
        },
        headerStyle: {
          position: "sticky",
          top: "0px",
          backgroundColor: colors.SECONDARY ,
          color:colors.WHITE,
          fontSize: "0.8em",
          fontWeight: 'bold ',
          fontFamily: 'Lucida Console", "Courier New", monospace'
        }
      }}
      localization={{body:{
        addTooltip: (t('add')),
        deleteTooltip: (t('delete')),
        editTooltip: (t('edit')),
        emptyDataSourceMessage: (
          (t('blank_message'))
      ),
      editRow: { 
        deleteText: (t('delete_message')),
        cancelTooltip: (t('cancel')),
        saveTooltip: (t('save')) 
        }, 
        },
        toolbar: {
          searchPlaceholder: (t('search')),
          exportTitle: (t('export')),
        },
        header: {
          actions: (t('actions')) 
      },
      pagination: {
        labelDisplayedRows: ('{from}-{to} '+ (t('of'))+ ' {count}'),
        firstTooltip: (t('first_page_tooltip')),
        previousTooltip: (t('previous_page_tooltip')),
        nextTooltip: (t('next_page_tooltip')),
        lastTooltip: (t('last_page_tooltip'))
      },
      }}
      editable={{
        onRowAdd: newData =>
          new Promise((resolve, reject) => {
            setTimeout(() => {
              // Validate all required fields including promo_validity
              const isValid = 
                newData.promo_name && 
                newData.promo_description && 
                newData.promo_discount_value !== undefined && 
                newData.promo_discount_value !== null &&
                newData.promo_discount_type && 
                newData.max_promo_discount_value !== undefined && 
                newData.max_promo_discount_value !== null &&
                newData.min_order !== undefined && 
                newData.min_order !== null &&
                newData.promo_usage_limit !== undefined && 
                newData.promo_usage_limit !== null &&
                newData.promo_validity !== undefined && 
                newData.promo_validity !== null &&
                parseFloat(newData.promo_discount_value) >= 0 &&
                parseFloat(newData.max_promo_discount_value) >= 0 &&
                parseFloat(newData.min_order) >= 0 &&
                parseFloat(newData.promo_usage_limit) >= 0;
              
              if(!isValid){
                alert(t('no_details_error'));
                reject();
              }else{
                newData['createdAt'] = new Date().getTime();
                // Convert promo_validity to timestamp (number)
                if (newData.promo_validity) {
                  if (typeof newData.promo_validity === 'number') {
                    newData['promo_validity'] = newData.promo_validity;
                  } else if (dayjs.isDayjs(newData.promo_validity)) {
                    newData['promo_validity'] = newData.promo_validity.valueOf();
                  } else {
                    newData['promo_validity'] = new Date(newData.promo_validity).getTime();
                  }
                } else {
                  newData['promo_validity'] = null;
                }
                dispatch(editPromo(newData,"Add"));
                resolve();
              }
            }, 600);
          }),
        onRowUpdate: (newData, oldData) =>
          settings.AllowCriticalEditsAdmin?
          new Promise((resolve, reject) => {
            setTimeout(() => {
              // Validate all required fields including promo_validity
              const isValid = 
                newData.promo_name && 
                newData.promo_description && 
                newData.promo_discount_value !== undefined && 
                newData.promo_discount_value !== null &&
                newData.promo_discount_type && 
                newData.max_promo_discount_value !== undefined && 
                newData.max_promo_discount_value !== null &&
                newData.min_order !== undefined && 
                newData.min_order !== null &&
                newData.promo_usage_limit !== undefined && 
                newData.promo_usage_limit !== null &&
                newData.promo_validity !== undefined && 
                newData.promo_validity !== null &&
                parseFloat(newData.promo_discount_value) >= 0 &&
                parseFloat(newData.max_promo_discount_value) >= 0 &&
                parseFloat(newData.min_order) >= 0 &&
                parseFloat(newData.promo_usage_limit) >= 0;
              
              if(!isValid){
                alert(t('no_details_error'));
                reject();
              }else{
                resolve();
                // Convert promo_validity to timestamp (number)
                if (newData.promo_validity) {
                  if (typeof newData.promo_validity === 'number') {
                    newData['promo_validity'] = newData.promo_validity;
                  } else if (dayjs.isDayjs(newData.promo_validity)) {
                    newData['promo_validity'] = newData.promo_validity.valueOf();
                  } else {
                    newData['promo_validity'] = new Date(newData.promo_validity).getTime();
                  }
                } else {
                  newData['promo_validity'] = null;
                }
                if(newData !== oldData){
                  delete newData.tableData;
                  dispatch(editPromo(newData,"Update"));
                }
              }
            }, 600);
          })
          :
          new Promise(resolve => {
            setTimeout(() => {
              resolve();
              alert(t('demo_mode'));
            }, 600);
          }),
        onRowDelete: oldData =>
          settings.AllowCriticalEditsAdmin?
          new Promise(resolve => {
            setTimeout(() => {
              resolve();
              dispatch(editPromo(oldData,"Delete"));
            }, 600);
          })
          :
          new Promise(resolve => {
            setTimeout(() => {
              resolve();
              alert(t('demo_mode'));
            }, 600);
          })
      }}
    />
  );
}
